import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, type HomeworkRow } from '../../lib/api.js'
import { useLocale } from '../../i18n/index.js'
import { Column, Main } from '../../classroom/Shell.js'
import { Bar, ButtonLink, ErrorLine, Skeleton, Title } from '../../classroom/ui.js'
import { titleFor } from './CoursePage.js'
import { nextHomework } from './StudentHomePage.js'
import { studentCopy } from './copy.js'
import { shellCopy, type ShellStrings } from './copyShell.js'

/**
 * „Zadania domowe” (Homework.png, redrawn to the brief): the homework of
 * every lesson the student has opened, grouped by where it stands. No dates
 * and nobody marks it but the machine — the brief's decision, 2026-10-17.
 */
export function HomeworkPage() {
  const locale = useLocale()
  const t = studentCopy(locale)
  const s = shellCopy(locale)
  const h = s.homework
  const { data, isPending, isError } = useQuery({ queryKey: ['course', 'homework'], queryFn: api.course.homework })

  const rows = [...(data ?? [])].sort((a, b) => a.courseSlug.localeCompare(b.courseSlug) || a.number - b.number)
  const todo = rows.filter((r) => r.done === 0)
  const doing = rows.filter((r) => r.done > 0 && r.done < r.parts)
  const done = rows.filter((r) => r.done >= r.parts).reverse()
  const next = nextHomework(data)
  const url = (r: HomeworkRow) => `/panel/kurs/${r.courseSlug}/${r.lessonSlug}/zadanie`

  return (
    <Column
      top={
        <>
          <p className="m-0 font-semibold">{s.nav.homework}</p>
          <span className="ml-auto text-class-label text-class-ink-faint">{h.topNote}</span>
        </>
      }
      bottom={
        next && (
          <ButtonLink to={url(next)} className="ml-auto max-desk:w-full">
            {next.done > 0 ? h.primaryResume(next.number) : h.primaryStart(next.number)}
          </ButtonLink>
        )
      }
    >
      <Main width={720}>
        <Title sub={h.sub}>{s.nav.homework}</Title>
        {isPending ? (
          <Skeleton lines={5} />
        ) : isError ? (
          <ErrorLine>{t.errors.load}</ErrorLine>
        ) : rows.length === 0 ? (
          <div>
            <p className="m-0 font-semibold">{h.empty}</p>
            <p className="m-0 text-class-gloss text-class-ink-faint">{h.emptyHint}</p>
          </div>
        ) : (
          <>
            <Group title={h.todo} rows={todo} kind="todo" url={url} h={h} locale={locale} lessonWord={t.course.lesson} />
            <Group title={h.doing} rows={doing} kind="doing" url={url} h={h} locale={locale} lessonWord={t.course.lesson} />
            <Group title={h.done} rows={done} kind="done" url={url} h={h} locale={locale} lessonWord={t.course.lesson} />
          </>
        )}
      </Main>
    </Column>
  )
}

function Group({
  title,
  rows,
  kind,
  url,
  h,
  locale,
  lessonWord,
}: {
  title: string
  rows: HomeworkRow[]
  kind: 'todo' | 'doing' | 'done'
  url: (r: HomeworkRow) => string
  h: ShellStrings['homework']
  locale: string
  lessonWord: string
}) {
  if (rows.length === 0) return null
  return (
    <section className="flex flex-col">
      <h2 className="cl-heading m-0 mb-3 text-class-subheading desk:text-class-heading">
        {title}
        <span className="ml-2 font-class-text text-class-gloss text-class-ink-faint">{rows.length}</span>
      </h2>
      <div className="flex flex-col [&>*+*]:border-t [&>*+*]:border-class-line">
        {rows.map((r) => {
          const own = titleFor(r, locale)
          return (
            <Link
              key={`${r.courseSlug}/${r.lessonSlug}`}
              to={url(r)}
              className="flex min-h-[76px] items-center gap-4 rounded-class-btn px-3 py-3 !text-class-ink hover:bg-class-field-soft/60"
            >
              <span
                className={[
                  'inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full text-class-gloss font-semibold',
                  kind === 'done' ? 'bg-class-correct-100 text-class-correct' : 'bg-class-field-soft text-class-sky-700',
                ].join(' ')}
              >
                {kind === 'done' ? '✓' : r.number}
              </span>
              <span className="min-w-0 flex-1 leading-snug">
                <span className="block truncate font-semibold">
                  {lessonWord} {r.number} · <span lang="pl">{r.title}</span>
                  {own !== r.title && <span className="font-normal text-class-ink-faint"> · {own}</span>}
                </span>
                <span className="block text-class-gloss text-class-ink-faint">
                  {kind === 'todo'
                    ? h.partsMeta(r.parts)
                    : kind === 'doing'
                      ? h.progress(r.done, r.parts)
                      : r.max > 0
                        ? h.score(r.score, r.max)
                        : h.doneNoScore}
                </span>
                {kind === 'doing' && <Bar value={(r.done / r.parts) * 100} className="mt-2 w-40" />}
              </span>
              <span className="whitespace-nowrap font-semibold text-class-sky-700">
                {kind === 'todo' ? h.start : kind === 'doing' ? h.resume : h.view} ›
              </span>
            </Link>
          )
        })}
      </div>
    </section>
  )
}
